import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import Swal from 'sweetalert2';

interface SousBien {
  id: number;
  name: string;
  image_url?: string;
  imagesList?: string[];
  category?: string;
  prix?: number;
  displayOrder?: number;
  orderIndex?: number;
  createdAt?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-list-agence-sous-bien',
  templateUrl: './list-agence-sous-bien.component.html',
  styleUrls: ['./list-agence-sous-bien.component.scss']
})
export class ListAgenceSousBienComponent implements OnInit {
  id: number; // Bien ID
  agencyId: string | null = null; // Agency ID extracted from parent bien
  sousBiens: SousBien[] = [];
  bienParent: any = null;
  isLoading = false;

  constructor(
    private sousBienService: SousBienService,
    private awsService: AwscontrolService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const idParam = params.get('id');
      this.id = idParam ? +idParam : 0;

      if (this.id <= 0) {
        Swal.fire({
          title: 'Erreur',
          text: 'ID du bien invalide.',
          icon: 'error'
        });
        this.location.back();
        return;
      }

      this.loadSousBiens();
    });
  }

  loadSousBiens(): void {
    this.isLoading = true;
    this.sousBienService.getBiensById(this.id).subscribe({
      next: (data: any) => {
        this.bienParent = data;
        // Extract agency ID from the parent bien
        if (data.user?.id) {
          this.agencyId = data.user.id.toString();
        } else if (data.userId) {
          this.agencyId = data.userId.toString();
        } else if (data.agencyId) {
          this.agencyId = data.agencyId.toString();
        }

        const rawSousBiens = Array.isArray(data?.sous_biens) ? data.sous_biens : [];
        this.sousBiens = rawSousBiens;
        this.initializeOrderIndexes();
        this.isLoading = false;
        console.log('Sous-biens chargés:', this.sousBiens);
        console.log('Agency ID extracted:', this.agencyId);
      },
      error: (err) => {
        console.error('Erreur chargement sous-biens:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de charger les sous-biens.',
          icon: 'error'
        });
      }
    });
  }

  private initializeOrderIndexes(): void {
    const hasOrder = this.sousBiens.some(s => s.displayOrder !== undefined && s.displayOrder !== null);
    if (hasOrder) {
      this.sousBiens.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    } else {
      this.sousBiens.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateA - dateB;
      });
    }
    this.sousBiens.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
  }

  // ============================================================
  // ORDERING METHODS (Batch update)
  // ============================================================

  private saveAllOrdersBatch(): void {
    if (this.sousBiens.length === 0) return;
    this.isLoading = true;
    const updates = this.sousBiens.map(bien => ({
      id: bien.id,
      displayOrder: bien.displayOrder!
    }));
    this.sousBienService.updateSousBiensOrderBatch(updates).subscribe({
      next: () => {
        this.isLoading = false;
        Swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 1500,
          icon: 'success',
          title: 'Ordre mis à jour'
        });
      },
      error: (err) => {
        console.error('Batch update error:', err);
        this.isLoading = false;
        Swal.fire({
          title: 'Erreur',
          text: 'Impossible de sauvegarder l\'ordre. Rechargement...',
          icon: 'error'
        });
        this.loadSousBiens();
      }
    });
  }

  private updateOrdersAndSave(): void {
    this.sousBiens.forEach((item, idx) => {
      item.orderIndex = idx + 1;
      item.displayOrder = idx;
    });
    this.saveAllOrdersBatch();
  }

  drop(event: CdkDragDrop<SousBien[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.sousBiens, event.previousIndex, event.currentIndex);
    this.updateOrdersAndSave();
  }

  moveUp(index: number): void {
    if (index === 0) return;
    [this.sousBiens[index], this.sousBiens[index - 1]] = [this.sousBiens[index - 1], this.sousBiens[index]];
    this.updateOrdersAndSave();
  }

  moveDown(index: number): void {
    if (index === this.sousBiens.length - 1) return;
    [this.sousBiens[index], this.sousBiens[index + 1]] = [this.sousBiens[index + 1], this.sousBiens[index]];
    this.updateOrdersAndSave();
  }

  // ============================================================
  // DELETE with AWS image removal
  // ============================================================

  deletesousBiens(sousBien: SousBien): void {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le !'
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Suppression en cours...',
          text: 'Veuillez patienter',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => Swal.showLoading()
        });

        this.deleteAllImagesFromS3(sousBien)
          .then(() => {
            return this.sousBienService.deleteSousBien(sousBien.id).toPromise();
          })
          .then(() => {
            Swal.close();
            Swal.fire({
              title: 'Supprimé !',
              text: 'Le sous-bien et ses images ont été supprimés.',
              icon: 'success'
            });
            this.sousBiens = this.sousBiens.filter(s => s.id !== sousBien.id);
            this.updateOrdersAndSave();
          })
          .catch((err) => {
            Swal.close();
            console.error('Erreur lors de la suppression:', err);
            this.sousBienService.deleteSousBien(sousBien.id).subscribe({
              next: () => {
                Swal.fire({
                  title: 'Supprimé !',
                  text: 'Le sous-bien a été supprimé (images non supprimées).',
                  icon: 'success'
                });
                this.sousBiens = this.sousBiens.filter(s => s.id !== sousBien.id);
                this.updateOrdersAndSave();
              },
              error: (err2) => {
                Swal.fire({
                  title: 'Erreur',
                  text: 'La suppression a échoué.',
                  icon: 'error'
                });
                console.error('Erreur suppression sous-bien:', err2);
              }
            });
          });
      }
    });
  }

  private async deleteAllImagesFromS3(sousBien: SousBien): Promise<void> {
    const imageUrls: string[] = [];

    if (sousBien.image_url && sousBien.image_url.trim() !== '') {
      imageUrls.push(sousBien.image_url);
    }

    if (sousBien.imagesList && Array.isArray(sousBien.imagesList)) {
      sousBien.imagesList.forEach((url: string) => {
        if (url && url.trim() !== '') {
          imageUrls.push(url);
        }
      });
    }

    if (imageUrls.length === 0) {
      return;
    }

    const deletePromises = imageUrls.map(url =>
      this.awsService.deleteFile(url).toPromise().catch(err => {
        console.warn(`Failed to delete image ${url}:`, err);
        return Promise.resolve();
      })
    );

    await Promise.all(deletePromises);
  }

  // ============================================================
  // NAVIGATION
  // ============================================================

  navigateBack(): void {
    if (this.agencyId) {
      this.router.navigate(['/list-agence-biens', this.agencyId]);
    } else {
      // Fallback: go back to previous page if agency ID not found
      this.location.back();
    }
  }
}